// features/lab-library-feature.js
// ═══════════════════════════════════════════════════════════
// Lab Library — Shift's substrate viewer.
//
// Graph view v3: mesh network
//   - 8 × 8 cell grid (domain × idea-type)
//   - Bubble size = atom count in that cell
//   - Edges = aggregated connections between cells
//   - Click cell → right panel with atom list
//   - Click atom → full AtomDetail
//
// Per §19 depersonalization governance: practitioner names only
// visible to the repo owner (OWNER_EMAIL).
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useRef } from 'react';

// ─── Owner gate ───────────────────────────────────────────────
const OWNER_EMAIL = 'timmai1004@gmail.com';

// ─── Base palette ─────────────────────────────────────────────
const C = {
  steel: '#6b8fa8', gold: '#c8a96e', sage: '#7a9e82', terra: '#c46050',
  teal: '#4db8b8', purple: '#9b8fc4', sand: '#b8956a',
  text1: '#d4d4d4', text2: '#a8a8a8', text3: '#888', text4: '#666',
  bg0: '#07070a', bg2: '#111118', bg3: '#171723',
  border3: '#272736', border4: '#1e1e2a',
};

// ─── Domain → color (PRIMARY encoding) ───────────────────────
function domainColor(domain) {
  const d = (domain || '').toLowerCase();
  if (d.includes('marketing') || d.includes('advertis') || d.includes('referral') || d.includes('brand')) return C.gold;
  if (d.includes('sales') || d.includes('closing') || d.includes('prospect') || d.includes('selling')) return C.sage;
  if (d.includes('copy') || d.includes('headline') || d.includes('direct mail') || d.includes('hook')) return C.teal;
  if (d.includes('consult') || d.includes('coach') || d.includes('advisory') || d.includes('professional')) return C.steel;
  if (d.includes('pric') || d.includes('fee') || d.includes('value-based') || d.includes('premium')) return C.purple;
  if (d.includes('mindset') || d.includes('psych') || d.includes('belief') || d.includes('mental')) return C.purple;
  if (d.includes('manag') || d.includes('operat') || d.includes('system') || d.includes('team') || d.includes('leader')) return C.sand;
  if (d.includes('strateg') || d.includes('business model') || d.includes('competitive') || d.includes('growth')) return '#8fa88f';
  return '#555570';
}

// ─── Mesh grid constants ──────────────────────────────────────
const MESH_DOMAINS = ['marketing', 'sales', 'copywriting', 'pricing', 'consulting', 'mindset', 'management', 'strategy'];
const MESH_TYPES   = ['principle', 'framework', 'heuristic', 'technique', 'distinction', 'vocabulary', 'anti_pattern', 'case'];

const DOMAIN_LABELS = {
  marketing: 'Marketing', sales: 'Sales', copywriting: 'Copywriting', pricing: 'Pricing',
  consulting: 'Consulting', mindset: 'Mindset', management: 'Management', strategy: 'Strategy',
};
const TYPE_LABELS = {
  principle: 'Principles', framework: 'Frameworks', heuristic: 'Heuristics',
  technique: 'Techniques', distinction: 'Distinctions', vocabulary: 'Vocabulary',
  anti_pattern: 'Anti-Patterns', case: 'Cases',
};
const DOMAIN_COLORS = {
  marketing: C.gold, sales: C.sage, copywriting: C.teal, pricing: C.purple,
  consulting: C.steel, mindset: C.purple, management: C.sand, strategy: '#8fa88f',
};

const SVG_W  = 1080;
const SVG_H  = 780;
const PAD    = { top: 62, left: 118, right: 16, bottom: 28 };
const CELL_W = (SVG_W - PAD.left - PAD.right)  / MESH_DOMAINS.length; // ≈ 118
const CELL_H = (SVG_H - PAD.top  - PAD.bottom) / MESH_TYPES.length;   // ≈ 86

const meshCX = (domain) => PAD.left + MESH_DOMAINS.indexOf(domain) * CELL_W + CELL_W / 2;
const meshCY = (type)   => PAD.top  + MESH_TYPES.indexOf(type)   * CELL_H + CELL_H / 2;

// SVG quadratic bezier between two cell centres (gently arced)
function edgePath(x1, y1, x2, y2) {
  const mx  = (x1 + x2) / 2;
  const my  = (y1 + y2) / 2;
  const dx  = x2 - x1;
  const dy  = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  const c   = len * 0.10; // arc strength
  return `M ${x1} ${y1} Q ${mx + (-dy / len) * c} ${my + (dx / len) * c} ${x2} ${y2}`;
}

// Edge visual properties per relation type
const EDGE_STYLE = {
  prerequisite_of:       { color: C.gold,   opacity: 0.50, width: 2.0, dash: null },
  sequel_to:             { color: C.gold,   opacity: 0.38, width: 1.4, dash: null },
  alongside_with:        { color: '#18182a', opacity: 0.10, width: 0.7, dash: null },
  contradicts:           { color: C.terra,  opacity: 0.60, width: 1.8, dash: '4 3' },
  cross_corpus_alignment:{ color: C.teal,   opacity: 0.75, width: 2.5, dash: '8 4' },
};

// ─── Detail panel helpers ─────────────────────────────────────
const GURU_BADGE  = { 'dan-kennedy': 'K', 'chet-holmes': 'H', 'alan-weiss': 'W', 'jay-abraham': 'A' };
const GURU_COLOR  = { 'dan-kennedy': C.gold, 'chet-holmes': C.sage, 'alan-weiss': C.steel, 'jay-abraham': C.purple };
const GURU_LABELS = { 'dan-kennedy': 'Kennedy', 'chet-holmes': 'Holmes', 'alan-weiss': 'Weiss', 'jay-abraham': 'Abraham' };

const BAC_LABELS = {
  worldview: 'Worldview', decision_rule: 'Decision Rule',
  anti_pattern: 'Anti-Pattern', case_evidence: 'Evidence', vocabulary: 'Vocabulary',
};

const TYPE_COLORS = {
  principle: C.gold, framework: C.steel, heuristic: C.sage,
  technique: C.sage, anti_pattern: C.terra, case: C.purple,
  vocabulary: C.teal, distinction: C.steel,
};

// ─── Main feature ─────────────────────────────────────────────
export default function LabLibraryFeature({ session, userRecord }) {
  const [view, setView]           = useState('browse');
  const [selectedAtom, setSelectedAtom] = useState(null);
  const accessToken = session?.access_token;
  const isOwner     = userRecord?.email === OWNER_EMAIL;

  return (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column',
      overflow: 'hidden', minHeight: 0,
      background: C.bg0, color: C.text1,
      fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
    }}>
      <LibraryHeader view={view} onView={(v) => { setView(v); setSelectedAtom(null); }} />

      {/* Browse — full-width */}
      {view === 'browse' && !selectedAtom && (
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          <BrowsePanel accessToken={accessToken} onSelectAtom={setSelectedAtom} selectedSlug={null} wide isOwner={isOwner} />
        </div>
      )}

      {/* Browse — with detail */}
      {view === 'browse' && selectedAtom && (
        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          <div style={{ flex: '0 0 360px', borderRight: `1px solid ${C.border3}`, overflowY: 'auto', minHeight: 0, background: C.bg0 }}>
            <BrowsePanel accessToken={accessToken} onSelectAtom={setSelectedAtom} selectedSlug={selectedAtom?.slug} wide={false} isOwner={isOwner} />
          </div>
          <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, background: C.bg2 }}>
            <AtomDetail slug={selectedAtom.slug} accessToken={accessToken} userRecord={userRecord} onNavigate={setSelectedAtom} onClose={() => setSelectedAtom(null)} />
          </div>
        </div>
      )}

      {/* Search */}
      {view === 'search' && (
        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          <div style={{ flex: '0 0 360px', borderRight: `1px solid ${C.border3}`, overflowY: 'auto', minHeight: 0, background: C.bg0 }}>
            <SearchPanel accessToken={accessToken} onSelectAtom={setSelectedAtom} selectedSlug={selectedAtom?.slug} />
          </div>
          <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, background: C.bg2 }}>
            {selectedAtom
              ? <AtomDetail slug={selectedAtom.slug} accessToken={accessToken} userRecord={userRecord} onNavigate={setSelectedAtom} onClose={() => setSelectedAtom(null)} />
              : <LibraryPlaceholder totalAtoms={7390} />
            }
          </div>
        </div>
      )}

      {/* Graph — mesh network */}
      {view === 'graph' && (
        <MeshView accessToken={accessToken} isOwner={isOwner} userRecord={userRecord} />
      )}

      {/* Playbooks */}
      {view === 'mocs' && (
        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          <div style={{ flex: '0 0 360px', borderRight: `1px solid ${C.border3}`, overflowY: 'auto', minHeight: 0, background: C.bg0 }}>
            <MOCList accessToken={accessToken} onSelectAtom={setSelectedAtom} />
          </div>
          <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, background: C.bg2 }}>
            <LibraryPlaceholder totalAtoms={7390} />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Library header ───────────────────────────────────────────
function LibraryHeader({ view, onView }) {
  const tabs = [
    { id: 'browse', label: 'Browse' },
    { id: 'search', label: 'Search' },
    { id: 'graph',  label: 'Mesh'   },
    { id: 'mocs',   label: 'Playbooks' },
  ];
  return (
    <div style={{ height: 44, display: 'flex', alignItems: 'center', gap: 2, padding: '0 16px', borderBottom: `1px solid ${C.border4}`, background: C.bg0, flexShrink: 0 }}>
      {tabs.map(t => (
        <button key={t.id} type="button" onClick={() => onView(t.id)}
          style={{ padding: '5px 14px', fontSize: 12, cursor: 'pointer', borderRadius: 5, fontWeight: view === t.id ? 600 : 400, transition: 'all 0.12s', background: view === t.id ? `${C.steel}22` : 'transparent', border: `1px solid ${view === t.id ? C.steel + '55' : 'transparent'}`, color: view === t.id ? C.text1 : C.text4 }}
          onMouseEnter={e => { if (view !== t.id) { e.currentTarget.style.color = C.text2; e.currentTarget.style.background = `${C.steel}0d`; } }}
          onMouseLeave={e => { if (view !== t.id) { e.currentTarget.style.color = C.text4; e.currentTarget.style.background = 'transparent'; } }}
        >{t.label}</button>
      ))}
      <div style={{ marginLeft: 'auto', fontSize: 11, color: C.text4 }}>7,351+ atoms · v3.2</div>
    </div>
  );
}

// ─── Mesh view ────────────────────────────────────────────────
function MeshView({ accessToken, isOwner, userRecord }) {
  const [meshData, setMeshData]       = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [selectedCell, setSelectedCell] = useState(null); // { domain, type, count, atoms[] }
  const [selectedAtom, setSelectedAtom] = useState(null); // { slug, label }

  useEffect(() => {
    setLoading(true);
    fetch('/api/library/mesh', {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then(r => r.json())
      .then(data => { setMeshData(data); setLoading(false); })
      .catch(e => { console.error('[mesh]', e); setError('Failed to load mesh data'); setLoading(false); });
  }, [accessToken]);

  if (loading) return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, color: C.text4 }}>
      <div style={{ fontSize: 13 }}>Building knowledge mesh…</div>
      <div style={{ fontSize: 11, opacity: 0.6 }}>Aggregating 7,300+ atoms across 8 domains</div>
    </div>
  );

  if (error) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.terra, fontSize: 13 }}>{error}</div>
  );

  return (
    <div style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
      {/* Mesh SVG — scrollable */}
      <div style={{ flex: 1, overflow: 'auto', minHeight: 0, position: 'relative' }}>
        {meshData && (
          <MeshSVG
            cells={meshData.cells}
            edges={meshData.edges}
            selectedCell={selectedCell}
            onCellClick={(cell) => { setSelectedCell(cell); setSelectedAtom(null); }}
          />
        )}
      </div>

      {/* Right panel — cell atoms */}
      {selectedCell && !selectedAtom && (
        <CellPanel
          cell={selectedCell}
          isOwner={isOwner}
          onSelectAtom={setSelectedAtom}
          onClose={() => setSelectedCell(null)}
        />
      )}

      {/* Right panel — atom detail */}
      {selectedAtom && (
        <div style={{ flex: '0 0 440px', borderLeft: `1px solid ${C.border3}`, overflowY: 'auto', minHeight: 0, background: C.bg2 }}>
          <AtomDetail
            slug={selectedAtom.slug}
            accessToken={accessToken}
            userRecord={userRecord}
            onNavigate={(a) => setSelectedAtom(a)}
            onClose={() => setSelectedAtom(null)}
          />
        </div>
      )}
    </div>
  );
}

// ─── Mesh SVG (pure React, no D3 force) ──────────────────────
function MeshSVG({ cells, edges, selectedCell, onCellClick }) {
  const [hoveredKey, setHoveredKey]         = useState(null);
  const [edgeTooltip, setEdgeTooltip]       = useState(null);
  const containerRef                        = useRef(null);

  // Build lookup & radius scale
  const cellLookup = {};
  for (const c of cells) cellLookup[`${c.domain}|${c.type}`] = c;

  const maxCount = cells.reduce((m, c) => Math.max(m, c.count), 1);
  const maxR = Math.min(CELL_W, CELL_H) / 2 * 0.85; // max ~42px
  const rScale = (count) => Math.max(5, Math.min(maxR, Math.sqrt(count / maxCount) * maxR));

  const selKey = selectedCell ? `${selectedCell.domain}|${selectedCell.type}` : null;

  // Handle edge tooltip positioning relative to container
  const handleEdgeEnter = (e, edge) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setEdgeTooltip({ x: e.clientX - rect.left + 10, y: e.clientY - rect.top - 30, edge });
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-block', minWidth: SVG_W }}>
      <svg width={SVG_W} height={SVG_H} style={{ display: 'block' }}>

        {/* ── Subtle grid dots at every cell centre ── */}
        {MESH_DOMAINS.map(domain =>
          MESH_TYPES.map(type => {
            const x = meshCX(domain);
            const y = meshCY(type);
            const has = !!cellLookup[`${domain}|${type}`];
            return (
              <circle key={`dot-${domain}-${type}`}
                cx={x} cy={y} r={has ? 0 : 2.5}
                fill="#1a1a2a" fillOpacity={0.9}
              />
            );
          })
        )}

        {/* ── Faint grid lines ── */}
        {MESH_DOMAINS.map((d, i) => (
          <line key={`vl-${d}`}
            x1={meshCX(d)} y1={PAD.top - 6}
            x2={meshCX(d)} y2={SVG_H - PAD.bottom + 6}
            stroke="#ffffff" strokeWidth={0.4} strokeOpacity={0.04}
          />
        ))}
        {MESH_TYPES.map((t, i) => (
          <line key={`hl-${t}`}
            x1={PAD.left - 8} y1={meshCY(t)}
            x2={SVG_W - PAD.right + 6} y2={meshCY(t)}
            stroke="#ffffff" strokeWidth={0.4} strokeOpacity={0.04}
          />
        ))}

        {/* ── Column labels (domain) ── */}
        {MESH_DOMAINS.map(domain => (
          <text key={`col-${domain}`}
            x={meshCX(domain)} y={PAD.top - 14}
            textAnchor="middle"
            fontSize={10} fontWeight={700} letterSpacing="0.05em"
            fill={DOMAIN_COLORS[domain] || C.text4} fillOpacity={0.9}
          >
            {(DOMAIN_LABELS[domain] || domain).toUpperCase()}
          </text>
        ))}

        {/* ── Row labels (idea type) ── */}
        {MESH_TYPES.map(type => (
          <text key={`row-${type}`}
            x={PAD.left - 12} y={meshCY(type) + 4}
            textAnchor="end"
            fontSize={10} fontWeight={500}
            fill={C.text4}
          >
            {TYPE_LABELS[type] || type}
          </text>
        ))}

        {/* ── Edges (drawn behind bubbles) ── */}
        {edges.map((edge, i) => {
          const x1 = meshCX(edge.source.domain);
          const y1 = meshCY(edge.source.type);
          const x2 = meshCX(edge.target.domain);
          const y2 = meshCY(edge.target.type);
          if (x1 === x2 && y1 === y2) return null;
          const style = EDGE_STYLE[edge.relation_type] || EDGE_STYLE.alongside_with;
          const thickness = Math.max(style.width * 0.5, Math.min(style.width * 2, style.width * Math.log(edge.count + 1)));

          const srcKey = `${edge.source.domain}|${edge.source.type}`;
          const tgtKey = `${edge.target.domain}|${edge.target.type}`;
          const isHighlighted = selKey === srcKey || selKey === tgtKey ||
                                hoveredKey === srcKey || hoveredKey === tgtKey;

          return (
            <g key={`e-${i}`}>
              {/* Wide transparent hit area for hovering */}
              <path
                d={edgePath(x1, y1, x2, y2)}
                fill="none" stroke="transparent" strokeWidth={12}
                style={{ cursor: 'default' }}
                onMouseEnter={(e) => handleEdgeEnter(e, edge)}
                onMouseLeave={() => setEdgeTooltip(null)}
              />
              {/* Visible edge */}
              <path
                d={edgePath(x1, y1, x2, y2)}
                fill="none"
                stroke={style.color}
                strokeWidth={isHighlighted ? thickness * 1.6 : thickness}
                strokeOpacity={isHighlighted ? Math.min(style.opacity * 2, 0.9) : style.opacity}
                strokeDasharray={style.dash || undefined}
                style={{ pointerEvents: 'none', transition: 'stroke-opacity 0.15s, stroke-width 0.15s' }}
              />
            </g>
          );
        })}

        {/* ── Cell bubbles ── */}
        {cells.map(cell => {
          const x = meshCX(cell.domain);
          const y = meshCY(cell.type);
          const r = rScale(cell.count);
          const col = DOMAIN_COLORS[cell.domain] || C.steel;
          const key = `${cell.domain}|${cell.type}`;
          const isSelected = selKey === key;
          const isHovered  = hoveredKey === key;
          const dimmed     = selKey && !isSelected && !isHovered;

          return (
            <g key={key}
              style={{ cursor: 'pointer' }}
              onClick={() => onCellClick(cell)}
              onMouseEnter={() => setHoveredKey(key)}
              onMouseLeave={() => setHoveredKey(null)}
            >
              {/* Selection ring */}
              {isSelected && (
                <circle cx={x} cy={y} r={r + 7}
                  fill="none" stroke={col} strokeWidth={1.5} strokeOpacity={0.6}
                  strokeDasharray="4 3"
                />
              )}
              {/* Main bubble */}
              <circle cx={x} cy={y} r={r}
                fill={col}
                fillOpacity={isSelected ? 0.88 : isHovered ? 0.75 : dimmed ? 0.20 : 0.55}
                stroke={col}
                strokeWidth={isSelected ? 2 : 1}
                strokeOpacity={isSelected ? 0.9 : isHovered ? 0.8 : 0.4}
                style={{ transition: 'fill-opacity 0.15s, stroke-opacity 0.15s' }}
              />
              {/* Count label inside bubble */}
              {r >= 14 && (
                <text x={x} y={y + 1}
                  textAnchor="middle" dominantBaseline="middle"
                  fontSize={Math.max(9, Math.min(14, r / 2.6))}
                  fontWeight={700} fill="#fff"
                  fillOpacity={dimmed ? 0.3 : 0.92}
                  style={{ pointerEvents: 'none' }}
                >
                  {cell.count}
                </text>
              )}
              {/* Type label below bubble */}
              <text x={x} y={y + r + 12}
                textAnchor="middle"
                fontSize={8.5} fill={col}
                fillOpacity={dimmed ? 0.2 : isSelected || isHovered ? 0.85 : 0.45}
                style={{ pointerEvents: 'none' }}
              >
                {TYPE_LABELS[cell.type]}
              </text>
            </g>
          );
        })}

        {/* ── Legend — bottom-left ── */}
        <g transform={`translate(${PAD.left}, ${SVG_H - 16})`}>
          {[
            { color: C.gold,  dash: null,   label: 'Builds on / leads to' },
            { color: C.terra, dash: '4 3',  label: 'Contradicts' },
            { color: C.teal,  dash: '8 4',  label: 'Cross-domain alignment' },
            { color: '#18182a', dash: null, label: 'Works alongside', opacity: 0.5 },
          ].map(({ color, dash, label, opacity = 1 }, i) => (
            <g key={label} transform={`translate(${i * 190}, 0)`}>
              <line x1={0} y1={-3} x2={20} y2={-3}
                stroke={color} strokeWidth={1.8} strokeDasharray={dash || undefined} strokeOpacity={opacity} />
              <text x={25} y={0} fontSize={9} fill={C.text4} fillOpacity={0.75}>{label}</text>
            </g>
          ))}
        </g>
      </svg>

      {/* Edge tooltip */}
      {edgeTooltip && (
        <div style={{
          position: 'absolute',
          left: edgeTooltip.x, top: edgeTooltip.y,
          background: C.bg2, border: `1px solid ${C.border3}`,
          borderRadius: 6, padding: '7px 11px', fontSize: 11,
          color: C.text2, zIndex: 30, pointerEvents: 'none',
          boxShadow: '0 4px 16px rgba(0,0,0,0.6)',
          whiteSpace: 'nowrap',
        }}>
          <span style={{ color: EDGE_STYLE[edgeTooltip.edge.relation_type]?.color || C.text3, fontWeight: 700 }}>
            {edgeTooltip.edge.relation_type?.replace(/_/g, ' ')}
          </span>
          <span style={{ color: C.text4, marginLeft: 8 }}>{edgeTooltip.edge.count} connections</span>
        </div>
      )}
    </div>
  );
}

// ─── Cell panel (right panel — atom list) ─────────────────────
function CellPanel({ cell, isOwner, onSelectAtom, onClose }) {
  const col = DOMAIN_COLORS[cell.domain] || C.steel;
  return (
    <div style={{
      flex: '0 0 320px', borderLeft: `1px solid ${C.border3}`,
      display: 'flex', flexDirection: 'column', minHeight: 0,
      background: C.bg0,
    }}>
      {/* Header */}
      <div style={{
        padding: '14px 16px', borderBottom: `1px solid ${C.border4}`,
        display: 'flex', alignItems: 'flex-start', gap: 8, flexShrink: 0,
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', color: col, textTransform: 'uppercase', marginBottom: 3 }}>
            {DOMAIN_LABELS[cell.domain] || cell.domain}
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.text1 }}>
            {TYPE_LABELS[cell.type] || cell.type}
          </div>
          <div style={{ fontSize: 11, color: C.text4, marginTop: 2 }}>
            {cell.count} atom{cell.count !== 1 ? 's' : ''}
            {cell.count > 60 && <span> · showing first 60</span>}
          </div>
        </div>
        <button type="button" onClick={onClose}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.text4, fontSize: 18, lineHeight: 1, padding: '0 2px', marginTop: -2 }}
          onMouseEnter={e => e.currentTarget.style.color = C.text2}
          onMouseLeave={e => e.currentTarget.style.color = C.text4}
        >×</button>
      </div>

      {/* Atom list */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {cell.atoms.map((a, i) => (
          <button key={a.slug || i} type="button"
            onClick={() => onSelectAtom(a)}
            style={{
              display: 'block', width: '100%', textAlign: 'left',
              padding: '10px 16px', background: 'transparent',
              border: 'none', borderBottom: `1px solid ${C.border4}`,
              cursor: 'pointer',
            }}
            onMouseEnter={e => e.currentTarget.style.background = `${C.steel}0d`}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: TYPE_COLORS[a.type] || C.text4 }}>
                {a.type?.replace('_', ' ')}
              </span>
              {isOwner && a.guru && (
                <span style={{ fontSize: 9, fontWeight: 700, color: GURU_COLOR[a.guru] || C.text4 }}>
                  · {GURU_BADGE[a.guru] || '?'}
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: C.text2, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {a.label || a.slug}
            </div>
          </button>
        ))}

        {cell.count > 60 && (
          <div style={{ padding: '12px 16px', fontSize: 11, color: C.text4, textAlign: 'center', borderTop: `1px solid ${C.border4}` }}>
            {cell.count - 60} more atoms — use Browse tab to see all<br />
            <span style={{ fontSize: 10, opacity: 0.6 }}>Filter by domain "{DOMAIN_LABELS[cell.domain]}" + type "{TYPE_LABELS[cell.type]}"</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Browse panel ─────────────────────────────────────────────
function BrowsePanel({ accessToken, onSelectAtom, selectedSlug, wide, isOwner }) {
  const [atoms, setAtoms]             = useState([]);
  const [total, setTotal]             = useState(0);
  const [page, setPage]               = useState(1);
  const [loading, setLoading]         = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filters, setFilters]         = useState({ guru: '', type: '', domain: '' });
  const perPage = wide ? 60 : 40;

  const load = useCallback(async (pg, f, append = false) => {
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, per_page: perPage });
      if (f.guru)   params.set('guru', f.guru);
      if (f.type)   params.set('type', f.type);
      if (f.domain) params.set('domain', f.domain);
      const res  = await fetch(`/api/library/atoms?${params}`, { headers: { Authorization: `Bearer ${accessToken}` } });
      const json = await res.json();
      if (append) setAtoms(prev => [...prev, ...(json.atoms || [])]);
      else        setAtoms(json.atoms || []);
      setTotal(json.total || 0);
    } catch (e) { console.error('[Library] browse:', e); }
    finally { setLoading(false); setLoadingMore(false); }
  }, [accessToken, perPage]);

  useEffect(() => { load(1, filters); setPage(1); }, [filters, load]);

  const handleLoadMore = () => { const next = page + 1; setPage(next); load(next, filters, true); };
  const hasMore = atoms.length < total;

  return (
    <div>
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border4}`, display: 'flex', flexDirection: wide ? 'row' : 'column', alignItems: wide ? 'center' : 'stretch', gap: wide ? 12 : 8, flexWrap: 'wrap' }}>
        {isOwner && (
          <FilterSelect label="Practitioner" value={filters.guru}
            options={[
              { value: '', label: 'All practitioners' }, { value: 'dan-kennedy', label: 'Kennedy' },
              { value: 'chet-holmes', label: 'Holmes' }, { value: 'alan-weiss', label: 'Weiss' },
              { value: 'jay-abraham', label: 'Abraham' },
            ]}
            onChange={v => setFilters(f => ({ ...f, guru: v }))}
          />
        )}
        <FilterSelect label="Type" value={filters.type}
          options={[
            { value: '', label: 'All types' }, { value: 'principle', label: 'Principle' },
            { value: 'framework', label: 'Framework' }, { value: 'heuristic', label: 'Heuristic' },
            { value: 'technique', label: 'Technique' }, { value: 'anti_pattern', label: 'Anti-Pattern' },
            { value: 'case', label: 'Case' }, { value: 'vocabulary', label: 'Vocabulary' },
            { value: 'distinction', label: 'Distinction' },
          ]}
          onChange={v => setFilters(f => ({ ...f, type: v }))}
        />
        <div style={{ marginLeft: wide ? 'auto' : 0, fontSize: 11, color: C.text4, whiteSpace: 'nowrap' }}>
          {loading ? 'Loading…' : `${total.toLocaleString()} atoms`}
        </div>
      </div>
      {loading ? (
        <div style={{ padding: 24, fontSize: 12, color: C.text4 }}>Loading…</div>
      ) : (
        <div style={wide ? { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, background: C.border4 } : {}}>
          {atoms.map(atom => (
            <AtomCard key={atom.slug} atom={atom} selected={atom.slug === selectedSlug} onClick={() => onSelectAtom({ slug: atom.slug })} wide={wide} isOwner={isOwner} />
          ))}
        </div>
      )}
      {hasMore && !loading && (
        <div style={{ padding: 16, textAlign: 'center' }}>
          <button type="button" onClick={handleLoadMore} disabled={loadingMore} style={{ padding: '8px 24px', fontSize: 12, cursor: 'pointer', background: 'transparent', border: `1px solid ${C.border3}`, borderRadius: 4, color: C.text3 }}>
            {loadingMore ? 'Loading…' : `Load more  (${atoms.length} / ${total.toLocaleString()})`}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Search panel ─────────────────────────────────────────────
function SearchPanel({ accessToken, onSelectAtom, selectedSlug }) {
  const [query, setQuery]     = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef           = useRef(null);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const res  = await fetch(`/api/library/search?q=${encodeURIComponent(q)}&limit=30`, { headers: { Authorization: `Bearer ${accessToken}` } });
      const json = await res.json();
      setResults(json.results || []);
    } catch (e) { console.error('[Library] search:', e); }
    finally { setLoading(false); }
  }, [accessToken]);

  const handleChange = (e) => {
    const q = e.target.value;
    setQuery(q);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(q), 320);
  };

  return (
    <div>
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border4}` }}>
        <input value={query} onChange={handleChange} placeholder="Search atoms…"
          style={{ width: '100%', padding: '7px 12px', fontSize: 13, background: C.bg3, border: `1px solid ${C.border3}`, borderRadius: 6, color: C.text1, outline: 'none', boxSizing: 'border-box' }}
        />
      </div>
      {loading && <div style={{ padding: 16, fontSize: 12, color: C.text4 }}>Searching…</div>}
      {!loading && query.length >= 2 && results.length === 0 && <div style={{ padding: 16, fontSize: 12, color: C.text4 }}>No results for "{query}"</div>}
      {results.map(atom => <AtomCard key={atom.slug} atom={atom} selected={atom.slug === selectedSlug} onClick={() => onSelectAtom({ slug: atom.slug })} showScore />)}
    </div>
  );
}

// ─── Atom card ────────────────────────────────────────────────
function AtomCard({ atom, selected, onClick, showScore, wide, isOwner }) {
  const color = TYPE_COLORS[atom.type] || C.text3;
  const guru  = GURU_LABELS[atom.guru] || atom.guru;
  return (
    <button type="button" onClick={onClick} style={{
      display: 'block', width: '100%', textAlign: 'left',
      padding: wide ? '14px 18px' : '10px 16px',
      border: 'none', cursor: 'pointer',
      background: selected ? `${C.steel}18` : C.bg0,
      borderLeft: `2px solid ${selected ? C.steel : 'transparent'}`,
      borderBottom: wide ? 'none' : `1px solid ${C.border4}`,
      transition: 'background 0.1s',
    }}
      onMouseEnter={e => { if (!selected) e.currentTarget.style.background = `${C.steel}0d`; }}
      onMouseLeave={e => { if (!selected) e.currentTarget.style.background = C.bg0; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color, textTransform: 'uppercase' }}>{atom.type?.replace('_', ' ')}</span>
        {isOwner && <><span style={{ fontSize: 10, color: C.text4 }}>·</span><span style={{ fontSize: 10, color: C.text4 }}>{guru}</span></>}
        {atom.domain && <><span style={{ fontSize: 10, color: C.text4 }}>·</span><span style={{ fontSize: 10, color: C.text4 }}>{atom.domain}</span></>}
        {showScore && atom.relevance_score && <span style={{ marginLeft: 'auto', fontSize: 10, color: C.steel }}>{(atom.relevance_score * 100).toFixed(0)}%</span>}
      </div>
      <p style={{ fontSize: wide ? 13 : 12, color: C.text2, lineHeight: 1.5, margin: 0, display: '-webkit-box', WebkitLineClamp: wide ? 3 : 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
        {atom.statement || atom.slug}
      </p>
    </button>
  );
}

// ─── Atom detail panel ────────────────────────────────────────
function AtomDetail({ slug, accessToken, userRecord, onNavigate, onClose }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true); setData(null);
    const userId = userRecord?.id;
    const params = new URLSearchParams({ render_mode: 'live' });
    if (userId) params.set('user_id', userId);
    fetch(`/api/library/atoms/${encodeURIComponent(slug)}?${params}`, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then(r => r.json())
      .then(json => { setData(json); setLoading(false); })
      .catch(e => { console.error('[Library] detail:', e); setLoading(false); });
  }, [slug, accessToken, userRecord]);

  if (loading) return <Loader />;
  if (!data?.atom) return <NotFound />;

  const { atom, compiled_truth, edges, backlinks } = data;
  const v3      = atom.v3_fields || {};
  const color   = TYPE_COLORS[atom.type] || C.text3;
  const isOwner = userRecord?.email === OWNER_EMAIL;

  return (
    <div style={{ padding: '28px 32px', maxWidth: 800, lineHeight: 1.6 }}>
      {onClose && (
        <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.text4, fontSize: 12, padding: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
          ← Back
        </button>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color, textTransform: 'uppercase', padding: '3px 8px', background: `${color}18`, borderRadius: 3 }}>{atom.type?.replace('_', ' ')}</span>
        {isOwner && <span style={{ fontSize: 12, color: C.text4 }}>{GURU_LABELS[atom.guru] || atom.guru}</span>}
        {atom.domain && <span style={{ fontSize: 12, color: C.text4 }}>{isOwner ? '· ' : ''}{atom.domain}</span>}
        {atom.behavioral_action_class && (
          <span style={{ marginLeft: 'auto', fontSize: 11, color: C.steel, padding: '2px 7px', border: `1px solid ${C.steel}44`, borderRadius: 3 }}>
            {BAC_LABELS[atom.behavioral_action_class] || atom.behavioral_action_class}
          </span>
        )}
      </div>
      <div style={{ fontSize: 17, fontWeight: 500, color: C.text1, lineHeight: 1.5, marginBottom: 20 }}>{compiled_truth?.content || v3.statement}</div>
      {v3.source_quote && (
        <blockquote style={{ margin: '0 0 20px 0', padding: '10px 0 10px 16px', borderLeft: `3px solid ${C.steel}55`, color: C.text3, fontStyle: 'italic', fontSize: 13 }}>
          "{v3.source_quote}"
        </blockquote>
      )}
      {v3.mechanism && <Section label="Why it works"><p style={{ margin: 0, fontSize: 14, color: C.text2 }}>{v3.mechanism}</p></Section>}
      {Array.isArray(v3.when_applies) && v3.when_applies.length > 0 && (
        <Section label="When to apply">
          <ul style={{ margin: 0, padding: '0 0 0 18px', fontSize: 13, color: C.text3 }}>{v3.when_applies.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </Section>
      )}
      {v3.persona_facet?.trait && (
        <Section label="Operator trait">
          <p style={{ margin: '0 0 10px', fontSize: 13, color: C.text2 }}>{v3.persona_facet.trait}</p>
          {Array.isArray(v3.persona_facet.manifests_as) && (
            <ul style={{ margin: 0, padding: '0 0 0 18px', fontSize: 12, color: C.text3 }}>{v3.persona_facet.manifests_as.map((m, i) => <li key={i}>{m}</li>)}</ul>
          )}
        </Section>
      )}
      {Array.isArray(v3.business_shape_applicability) && v3.business_shape_applicability.length > 0 && (
        <Section label="Fits these businesses">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{v3.business_shape_applicability.slice(0, 5).map((s, i) => <ShapeRow key={i} shape={s} />)}</div>
        </Section>
      )}
      {(edges?.length > 0 || backlinks?.length > 0) && (
        <Section label="Connected atoms">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {[...(edges || []), ...(backlinks || [])].slice(0, 12).map((e, i) => (
              <EdgeRow key={i} edge={e} onClick={() => onNavigate && onNavigate({ slug: e.atom?.slug || e.slug })} />
            ))}
          </div>
        </Section>
      )}
      {compiled_truth?.summary && (
        <Section label="Synthesis">
          <p style={{ margin: 0, fontSize: 13, color: C.text2, lineHeight: 1.7 }}>{compiled_truth.summary}</p>
        </Section>
      )}
      {compiled_truth && (
        <div style={{ marginTop: 24, padding: '12px 16px', background: `${C.steel}0a`, borderRadius: 6, border: `1px solid ${C.steel}22` }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: C.steel, textTransform: 'uppercase', marginBottom: 6 }}>In the graph</div>
          <button type="button"
            style={{ fontSize: 12, color: C.steel, background: 'transparent', border: `1px solid ${C.steel}44`, borderRadius: 4, padding: '5px 14px', cursor: 'pointer' }}
            onMouseEnter={e => { e.currentTarget.style.background = `${C.steel}18`; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            Open →
          </button>
        </div>
      )}
    </div>
  );
}

// ─── MOC list ─────────────────────────────────────────────────
function MOCList({ accessToken, onSelectAtom }) {
  const [mocs, setMocs]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/library/mocs', { headers: { Authorization: `Bearer ${accessToken}` } })
      .then(r => r.json())
      .then(json => { setMocs(json.mocs || []); setLoading(false); })
      .catch(e => { console.error('[Library] mocs:', e); setLoading(false); });
  }, [accessToken]);

  if (loading) return <Loader />;
  if (!mocs.length) return (
    <div style={{ padding: '24px 20px', fontSize: 13, color: C.text4, textAlign: 'center' }}>
      No playbooks yet.<br />
      <span style={{ fontSize: 11, color: C.text4, opacity: 0.6 }}>Playbooks surface thematic clusters across practitioners.</span>
    </div>
  );

  return (
    <div>
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border4}`, fontSize: 12, color: C.text3, fontWeight: 600 }}>
        {mocs.length} playbook{mocs.length !== 1 ? 's' : ''}
      </div>
      {mocs.map(m => (
        <button key={m.slug} type="button"
          onClick={() => onSelectAtom && onSelectAtom({ slug: m.slug })}
          style={{ display: 'block', width: '100%', textAlign: 'left', padding: '11px 16px', border: 'none', cursor: 'pointer', background: 'transparent', borderBottom: `1px solid ${C.border4}` }}
          onMouseEnter={e => { e.currentTarget.style.background = `${C.steel}0d`; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          <div style={{ fontSize: 12, fontWeight: 600, color: C.text1, marginBottom: 4 }}>{m.title}</div>
          <div style={{ fontSize: 11, color: C.text4, display: 'flex', gap: 10 }}>
            <span>{m.atom_count || 0} atoms</span>
            {m.domain && <span>{m.domain}</span>}
          </div>
        </button>
      ))}
    </div>
  );
}

// ─── Small helpers ────────────────────────────────────────────

function Section({ label, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.09em', color: C.text4, textTransform: 'uppercase', marginBottom: 8 }}>{label}</div>
      {children}
    </div>
  );
}

function ShapeRow({ shape }) {
  if (!shape) return null;
  const fits     = shape.fit === 'high' || shape.fit === 'very_high';
  const fitColor = fits ? C.sage : C.terra;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '6px 0', borderBottom: `1px solid ${C.border4}` }}>
      <div style={{ width: 6, height: 6, borderRadius: '50%', background: fitColor, marginTop: 5, flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, color: C.text2, marginBottom: 2 }}>
          {shape.business_type || shape.shape}
          {shape.stage && <span style={{ marginLeft: 6, fontSize: 10, color: C.text4 }}>· {shape.stage}</span>}
        </div>
        {shape.rationale && <div style={{ fontSize: 11, color: C.text4 }}>{shape.rationale}</div>}
      </div>
      {shape.fit && (
        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', color: fitColor, textTransform: 'uppercase', flexShrink: 0 }}>{shape.fit}</span>
      )}
    </div>
  );
}

function EdgeRow({ edge, onClick }) {
  if (!edge) return null;
  const rel   = edge.relation_type || edge.relation || '';
  const atom  = edge.atom || {};
  const color = { prerequisite_of: C.gold, sequel_to: C.gold, alongside_with: C.steel, contradicts: C.terra, cross_corpus_alignment: C.teal }[rel] || C.text4;
  const label = rel.replace(/_/g, ' ');
  return (
    <button type="button" onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 8, width: '100%',
      background: 'transparent', border: 'none', cursor: atom.slug ? 'pointer' : 'default',
      padding: '5px 0', textAlign: 'left', borderBottom: `1px solid ${C.border4}`,
    }}
      onMouseEnter={e => { if (atom.slug) e.currentTarget.style.background = `${C.steel}0a`; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >
      <div style={{ width: 28, height: 2, background: color, borderRadius: 1, flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 10, color, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: 12, color: C.text2, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {atom.statement || atom.slug || '—'}
        </div>
      </div>
    </button>
  );
}

function FilterSelect({ label, value, options, onChange }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {label && <span style={{ fontSize: 10, fontWeight: 600, color: C.text4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>}
      <select value={value} onChange={e => onChange(e.target.value)}
        style={{ padding: '5px 8px', fontSize: 12, background: C.bg3, border: `1px solid ${C.border3}`, borderRadius: 4, color: C.text2, cursor: 'pointer', outline: 'none', width: '100%' }}
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function Loader() {
  return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 120, fontSize: 12, color: C.text4 }}>Loading…</div>;
}

function NotFound() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, gap: 8 }}>
      <div style={{ fontSize: 13, color: C.text3 }}>Atom not found</div>
      <div style={{ fontSize: 11, color: C.text4 }}>It may have been removed or the slug is invalid.</div>
    </div>
  );
}

function LibraryPlaceholder({ totalAtoms }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12, padding: 32 }}>
      <div style={{ width: 42, height: 42, borderRadius: '50%', background: `${C.steel}18`, border: `1px solid ${C.steel}33`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: 18, color: C.steel }}>⬡</span>
      </div>
      <div style={{ fontSize: 14, fontWeight: 500, color: C.text3 }}>Select an atom</div>
      <div style={{ fontSize: 12, color: C.text4, textAlign: 'center', maxWidth: 280, lineHeight: 1.6 }}>
        {(totalAtoms || 7351).toLocaleString()}+ knowledge atoms across 8 domains.<br />
        Select one to read its compiled truth, context, and connections.
      </div>
    </div>
  );
}
